"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiFetcherProviderModal } from "../components/ApiFetcherProviderModal/ApiFetcherProviderModal";
import { ApiFetcherContext } from "../contexts/ApiFecher/ApiFetcher";
import { useLocale } from "../contexts/Locale";
import { getApiFetcher } from "../helpers/apiFetcher/getApiFetcher";
import useDisclosure from "../hooks/useDisclosure";
import { usePathname, useRouter } from "next/navigation";
import { useAuthState } from "../hooks/useAuth";

/**
 * @typedef {object} Props
 * @prop {string} [channelId]
 * @prop {boolean} [withModal]
 * @prop {string} signInPath
 * @prop {React.ReactNode} children
 */
/**
 * @param {Props} props
 */
export function ApiFetcherProvider({
  channelId,
  withModal = true,
  signInPath,
  children,
}) {
  const { open: openModal, setOpen } = useDisclosure();

  const { setAuthenticated } = useAuthState();

  const pathname = usePathname();

  const { locale } = useLocale();

  const { push } = useRouter();

  const [promiseState, setPromiseState] = useState(
    /** @type {{resolve: (value: boolean | PromiseLike<boolean>) => void, reject: (reason?: any) => void} | null} */ (
      null
    ),
  );

  const [challengeData, setChallengeData] = useState(
    /** @type {import("../hooks/useApiFetcher").TwoFactorAuthChallengeData | null} */ (
      null
    ),
  );

  useEffect(() => {
    // This cleanup function will run when the component unmounts
    return () => {
      if (promiseState) {
        // If a promise is still pending when the component is destroyed,
        // we must reject it to prevent a memory leak.
        promiseState.reject("Component unmounted");
      }
    };
  }, [promiseState]);

  const startChallenge = useCallback(
    /** @type {import("../hooks/useApiFetcher").TwoFactorAuthChallengeStartChallenge} */
    (challengeData) => {
      // si une promesse est en cours, on la rejette, on ne traite qu'une seule promesse à la fois
      if (promiseState !== null) {
        promiseState.reject?.("aborted");
      }
      return new Promise((resolve, reject) => {
        setChallengeData(challengeData);
        setOpen(true);
        setPromiseState({ resolve, reject });
      });
    },
    [promiseState, setOpen],
  );

  const handleOpenChange = useCallback(
    /** @param {import("@chakra-ui/react").DialogOpenChangeDetails} params */
    ({ open }) => {
      if (open) {
        setOpen(open);
      } else {
        setOpen(open);
        if (promiseState?.reject) {
          promiseState.reject("aborted");
          setPromiseState(null);
        }
        setPromiseState(null);
      }
    },
    [setOpen, promiseState],
  );

  const resolve = useCallback(
    /**
     * @param {boolean} isSuccess
     */
    (isSuccess) => {
      if (promiseState?.resolve) {
        // resolve the promise
        promiseState.resolve(isSuccess);
        // reset the promise state
        setPromiseState(null);
        // close the modal
        setOpen(false);
      }
    },
    [promiseState, setOpen],
  );

  const apiFetcher = useMemo(() => {
    return getApiFetcher({
      locale,
      channelId,
      startChallenge,
      onLogout: () => {
        setAuthenticated(false);
        push(`${signInPath}?next=${pathname}`);
      },
    });
  }, [
    channelId,
    locale,
    pathname,
    push,
    setAuthenticated,
    signInPath,
    startChallenge,
  ]);

  // #region return
  return (
    <ApiFetcherContext.Provider value={{ apiFetcher }}>
      {children}

      {withModal && (
        <ApiFetcherProviderModal
          challengeData={challengeData}
          resolve={resolve}
          openModal={openModal}
          handleOpenChange={handleOpenChange}
        />
      )}
    </ApiFetcherContext.Provider>
  );
}
